import { fromTheme, hooks, Icon, styled, Text, View } from 'lib_ui-primitives';
import { constants, globalConfig, nativeOnlyProperties } from 'lib_ui-services';
import lodash from 'lodash';
import { createElement as rc } from 'react';
import useDbView from '../../hooks/useDbView';
import useNetwork from '../../hooks/useNetwork';
import DataAggregationLimitAllSyncSizes from './DataAggregation.limitAllSyncSizes';
const { capitalize } = lodash;
const { useBbState } = hooks;

// limitAllSyncSizes is a per-tenant setting persisted into BbState by loadTenantSettings. Reading it
// through useBbState (rather than the non-reactive globalConfig() snapshot) lets the dispatcher
// re-render and switch to server aggregation when a cold sync delivers the setting post-mount —
// otherwise the local variant stays rendered for the whole session and the count never hits the
// server. Mirrors the Chart.js dispatcher.
const { SCOPE, VISIBILITY, DURATION } = constants.retention;
const TENANT_SETTING_RETENTION = {
    scope: SCOPE.GLOBAL,
    visibility: VISIBILITY.PER_TENANT,
    duration: DURATION.ALWAYS
};

const Label = styled(Text).attrs({ name: 'label' })`
    color: ${fromTheme('dataAggregation', 'fontColor')};
    font-size: ${({ centerLabelAbove, theme }) => (centerLabelAbove ? theme.fontSizeLabel : theme.fontSize)}px;
`;
Label.displayName = 'Label';

const AggregateText = styled(Text).attrs({ name: 'aggregate-text' })`
    color: ${fromTheme('dataAggregation', 'fontColor')};
    font-size: ${({ centerLabelAbove, theme }) => (centerLabelAbove ? theme.fontSizeLarge : theme.fontSize)}px;
`;
AggregateText.displayName = 'AggregateText';

const VerticalAggregateContainer = styled(View).attrs({ name: 'vertical-aggregate-container' })`
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-right: 6px;
    margin-left: 6px;
    margin-top: 6px;
    overflow: visible;
`;
VerticalAggregateContainer.displayName = 'VerticalAggregateContainer';

const HorizontalAggregateContainer = styled(View).attrs({ name: 'horizontal-aggregate-container' })`
    align-items: center;
    flex-direction: row;
    margin-right: 6px;
    margin-left: 6px;
`;
HorizontalAggregateContainer.displayName = 'HorizontalAggregateContainer';

const OfflineIcon = styled(Icon).attrs({ name: 'cloud-off', size: 'sm', color: 'currentColor' })`
    color: ${fromTheme('dataAggregation', 'fontColor')};
`;
OfflineIcon.displayName = 'OfflineIcon';

const _p = { useDbView, useNetwork, globalConfig, useBbState };
export const _private = _p;

function LocalDataAggregation(props) {
    const {
        hNode,
        hNode: { namespace, relation, aggregateOperation, title, id, centerLabelAbove }
    } = props;

    const { recordCount } = _p.useDbView(namespace, relation, `data-agg-${id}`, hNode);
    const { isOnline } = _p.useNetwork();

    const countDisplay = isOnline
        ? rc(AggregateText, null, recordCount.toString())
        : rc(OfflineIcon);

    // prettier-ignore
    return rc(centerLabelAbove ? VerticalAggregateContainer : HorizontalAggregateContainer, { id },
        rc(Label, {centerLabelAbove, ...nativeOnlyProperties({ellipsizeMode: 'clip', numberOfLines: 1})}, ((title + '') === '') ? `${capitalize(aggregateOperation)}:`: title),
        countDisplay
    );
}

export default function DataAggregation(props) {
    const {
        hNode: { aggregateOperation }
    } = props;
    if (aggregateOperation !== 'count') {
        throw new Error(
            `Aggregation operation of ${aggregateOperation} has not implemented yet. See dataView.DataAggregation.js`
        );
    }

    // Seed from globalConfig (already correct on warm loads) but read reactively from BbState so a
    // cold sync that delivers limitAllSyncSizes post-mount flips us to the server-aggregation variant
    // instead of leaving the local variant rendered for the whole session.
    const [limitAllSyncSizes] = _p.useBbState(
        _p.globalConfig().tenantSettings?.limitAllSyncSizes ?? false,
        'limitAllSyncSizes',
        TENANT_SETTING_RETENTION
    );
    const DataAggImpl = limitAllSyncSizes === true ? DataAggregationLimitAllSyncSizes : LocalDataAggregation;
    return rc(DataAggImpl, props);
}
